import { useState, useRef, useLayoutEffect } from "react";
import { Rocket, Users, Youtube, Star, ArrowUpRight } from "lucide-react";
import { useReveal } from "@/components/Reveal";

/* Community — Figma node 102:3047 (Frame 98), 1426 × 907.

   Scaled stage, same technique as the hero. Below lg the stage is dropped for a
   stacked layout.

   NOTE: the portrait is a transparent PNG — it must not be converted to JPEG or
   the alpha flattens to black behind the subject. */

const STAGE_W = 1426;
const STAGE_H = 907;
const BLUE = "#88ADDF";

// TODO: replace with the real WhatsApp community invite link (carried over from
// the previous landing page — it was a placeholder there too).
const WHATSAPP_COMMUNITY_URL = "https://chat.whatsapp.com/REPLACE_WITH_INVITE";

const FEATURES = [
  { icon: Rocket, title: "100+ Expert Mentors", sub: "Industry leaders guiding your journey" },
  { icon: Users, title: "20+ Skill Packages", sub: "Structured paths to accelerate growth" },
  { icon: Youtube, title: "1 Mission", sub: "Empower the next generation" },
];

const card = "rounded-[17.767px] bg-white px-5 py-[17px] shadow-[0_0_20px_0_rgba(0,0,0,0.08)]";

/* `compact` is the variant the mobile portrait floats: same content, smaller type
   and padding so five cards fit around the subject. The lg+ stage never passes
   it, so the desktop cards are untouched. */
const cardSm = "rounded-[14px] bg-white px-3 py-2.5 shadow-[0_0_20px_0_rgba(0,0,0,0.08)]";
const shell = (compact) => (compact ? cardSm : card);

function LiveSessionCard({ compact = false }) {
  return (
    <div className={`${shell(compact)} flex w-full flex-col ${compact ? "gap-1.5" : "gap-[9px]"}`}>
      <span
        className={`flex items-center justify-center rounded-[12.585px] bg-[#0C336E] font-poppins font-medium text-white ${
          compact ? "h-[24px] w-[80px] text-[10px]" : "h-[33px] w-[106px] text-[12px]"
        }`}
      >
        Live Session
      </span>
      <p className={`font-poppins text-black ${compact ? "text-[13px] leading-[16px]" : "text-[20px] leading-[22px]"}`}>
        Building Your Personal Brand
      </p>
      <span className={`font-poppins leading-4 text-[#0C336E] ${compact ? "text-[10px]" : "text-[12px]"}`}>Join Live →</span>
    </div>
  );
}

function CreatorCourseCard({ compact = false }) {
  return (
    <div className={`${shell(compact)} flex w-full flex-col ${compact ? "gap-1.5" : "gap-3"}`}>
      <span
        className={`flex items-center justify-center rounded-[12.585px] bg-[#11AD90] font-poppins font-medium text-white ${
          compact ? "h-[24px] w-[92px] text-[10px]" : "h-[33px] w-[120px] text-[12px]"
        }`}
      >
        Creator Course
      </span>
      <p className={`font-poppins text-black ${compact ? "text-[13px] leading-[16px]" : "text-[20px] leading-[21px]"}`}>
        Content Creation Mastery
      </p>
      <p className={`font-poppins leading-4 text-[#878787] ${compact ? "text-[10px]" : "text-[14px]"}`}>12 Modules • 36 Lessons</p>
      <p className={`font-poppins font-medium leading-4 text-[#11AD90] ${compact ? "text-[10px]" : "text-[14px]"}`}>+320 Learners</p>
    </div>
  );
}

function PodcastingCard({ compact = false }) {
  return (
    <div className={`${shell(compact)} flex w-full flex-col ${compact ? "gap-1" : "gap-[9px]"}`}>
      <p className={`font-poppins uppercase leading-4 text-[#99482F] ${compact ? "text-[10px]" : "text-[16px]"}`}>Podcasting</p>
      <p className={`font-poppins text-black ${compact ? "text-[19px] leading-[21px]" : "text-[32px] leading-[34px]"}`}>
        Create.<br />Publish.<br />Grow.
      </p>
      <span className={`font-poppins leading-4 text-[#99482F] ${compact ? "text-[10px]" : "text-[12px]"}`}>Explore →</span>
    </div>
  );
}

function CommunityCountCard({ compact = false }) {
  return (
    <div className={`${shell(compact)} flex w-full flex-col ${compact ? "gap-1.5" : "gap-3"}`}>
      <span
        className={`flex items-center justify-center rounded-[12.585px] bg-[#FFC807] font-poppins font-medium text-black ${
          compact ? "h-[24px] w-[92px] text-[10px]" : "h-[33px] w-[120px] text-[12px]"
        }`}
      >
        Community
      </span>
      <p className={`font-poppins text-black ${compact ? "text-[17px] leading-[18px]" : "text-[24px] leading-[19px]"}`}>2.8K+</p>
      <p className={`font-poppins leading-4 text-[#878787] ${compact ? "text-[10px]" : "text-[12px]"}`}>
        Creators in the GENRYZ Community
      </p>
      <div className={`flex items-center ${compact ? "gap-1.5" : "gap-2"}`}>
        <div className="flex -space-x-2">
          {["hero-avatar-1", "hero-avatar-2", "rishabh-2", "rishabh-3"].map((a) => (
            <span
              key={a}
              className={`overflow-hidden rounded-full border-2 border-white ${compact ? "h-[22px] w-[22px]" : "h-[30px] w-[30px]"}`}
            >
              <img src={`/images/${a}.jpg`} alt="" className="h-full w-full object-cover" loading="lazy" />
            </span>
          ))}
        </div>
        <span
          className={`flex items-center justify-center rounded-lg bg-[#F5F5F5] px-1 font-poppins text-[#757575] ${
            compact ? "h-5 min-w-[20px] text-[10px]" : "h-6 min-w-[24px] text-[14px]"
          }`}
        >
          +1
        </span>
      </div>
    </div>
  );
}

function FeaturedCreatorCard({ compact = false }) {
  return (
    <div
      className={`flex w-full items-center rounded-[17.767px] bg-white shadow-[0_0_20px_0_rgba(0,0,0,0.08)] ${
        compact ? "h-[132px] gap-3 rounded-[14px] p-2.5" : "h-[176px] max-w-[340px] gap-4 p-[17px]"
      }`}
    >
      <div className={`min-w-0 flex-1 overflow-hidden ${compact ? "h-[112px] rounded-[12px]" : "h-[142px] rounded-[17.767px]"}`}>
        <img src="/images/community-featured.jpg" alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className={`flex shrink-0 flex-col ${compact ? "w-[104px] gap-1.5" : "w-[128px] gap-2.5"}`}>
        <div className={`flex items-center ${compact ? "gap-1.5" : "gap-2"}`}>
          <span
            className={`flex shrink-0 items-center justify-center rounded-full bg-[#C419B9] ${
              compact ? "h-[22px] w-[22px]" : "h-[30px] w-[30px]"
            }`}
          >
            <Star size={compact ? 14 : 20} className="text-white" />
          </span>
          <span
            className={`font-poppins font-medium uppercase text-[#C419B9] ${
              compact ? "text-[10px] leading-[12px]" : "text-[14px] leading-[17px]"
            }`}
          >
            Featured<br />Creator
          </span>
        </div>
        <p className={`font-poppins text-black ${compact ? "text-[13px] leading-[16px]" : "text-[20px] leading-6"}`}>
          Featured in GenRYZ Spotlight
        </p>
        <span className={`font-poppins leading-4 text-[#C419B9] ${compact ? "text-[10px]" : "text-[12px]"}`}>View Feature →</span>
      </div>
    </div>
  );
}

/* The frame shows a zoomed crop of the source PNG; the size/offset percentages
   are Figma's fill transform copied verbatim. Both axes are set, so any host
   frame must keep the 937:788 aspect or the subject stretches. */
function PortraitImg() {
  return (
    <img
      src="/images/community-hero.png"
      alt=""
      className="absolute max-w-none"
      style={{ width: "208.7%", height: "165.43%", left: "-54.62%", top: "-45.56%" }}
    />
  );
}

function Portrait() {
  return (
    <div className="absolute overflow-hidden" style={{ left: 566, top: 119, width: 937, height: 788 }} aria-hidden>
      <PortraitImg />
    </div>
  );
}

function FeatureRow({ f }) {
  return (
    <div className="flex items-center gap-6">
      <span className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full" style={{ background: BLUE }}>
        <f.icon size={32} className="text-white" />
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="font-montserrat text-[20px] font-semibold text-black lg:text-[24px]">{f.title}</span>
        <span className="font-poppins text-[14px] text-[#5A5A5A]">{f.sub}</span>
      </span>
    </div>
  );
}

function JoinCta({ className = "" }) {
  return (
    <a
      href={WHATSAPP_COMMUNITY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex h-14 items-center justify-center gap-3 rounded-[45px] bg-white pl-[22px] pr-[11px] font-poppins text-[18px] text-black transition-all hover:brightness-95 active:scale-95 ${className}`}
    >
      Join Our Community
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-transform group-hover:scale-105"
        style={{ background: BLUE }}
      >
        <ArrowUpRight size={18} className="text-white" />
      </span>
    </a>
  );
}

export default function CommunitySection() {
  const stageRef = useRef(null);
  const [panelRef, inView] = useReveal();
  const [scale, setScale] = useState(0);

  // Elements hold at opacity 0 until the panel scrolls into view, then run one of
  // the shared keyframes with a stagger.
  const enter = (kind) => (inView ? kind : "opacity-0");

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / STAGE_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <section id="community" className="px-3 py-8 sm:px-4 lg:px-4 xl:py-10">
      <div ref={panelRef} className="mx-auto max-w-[1920px] overflow-hidden rounded-[24px] bg-[#E2ECFA]">

        {/* ── desktop stage ── */}
        <div
          ref={stageRef}
          className="relative hidden w-full lg:block"
          style={{ height: scale ? STAGE_H * scale : undefined }}
        >
          <div
            className="absolute left-0 top-0 origin-top-left"
            style={{
              width: STAGE_W,
              height: STAGE_H,
              transform: `scale(${scale || 1})`,
              visibility: scale ? "visible" : "hidden",
            }}
          >
            {/* First in DOM order so it paints behind every card — nothing here sets z-index. */}
            <Portrait />

            <div
              className={`${enter("anim-rise")} absolute flex items-center justify-center rounded-[45px] border border-black font-poppins text-[16px] uppercase text-black`}
              style={{ left: 81, top: 101, width: 158, height: 47 }}
            >
              Community
            </div>

            <div
              className={`${enter("anim-fade")} absolute rounded-[17.812px]`}
              style={{ left: 288, top: 166, width: 315, height: 83, background: BLUE, animationDelay: "0.3s" }}
              aria-hidden
            />
            <h2
              className={`${enter("anim-rise")} absolute font-poppins text-[60px] font-medium leading-[73px] text-black`}
              style={{ left: 81, top: 171, animationDelay: "0.06s" }}
            >
              Where&nbsp; Creators<br />Grow Together.
            </h2>

            <p
              className={`${enter("anim-rise")} absolute font-poppins text-[18px] font-light leading-[28px] text-black`}
              style={{ left: 82, top: 330, width: 551, animationDelay: "0.14s" }}
            >
              Join a thriving community of creators, mentors, and learners. Collaborate, connect, and grow through shared opportunities.
            </p>

            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className={`${enter("anim-rise")} absolute`}
                style={{ left: 81, top: 465 + i * 84, width: 352, animationDelay: `${0.2 + i * 0.06}s` }}
              >
                <FeatureRow f={f} />
              </div>
            ))}

            <div className={`${enter("anim-rise")} absolute`} style={{ left: 81, top: 751, animationDelay: "0.38s" }}>
              <JoinCta className="w-[270px]" />
            </div>

            <div className={`${enter("anim-pop")} absolute`} style={{ left: 735, top: 88, width: 190, animationDelay: "0.18s" }}><LiveSessionCard /></div>
            <div className={`${enter("anim-pop")} absolute`} style={{ left: 1130, top: 155, width: 220, animationDelay: "0.24s" }}><CreatorCourseCard /></div>
            <div className={`${enter("anim-pop")} absolute`} style={{ left: 668, top: 391, width: 190, animationDelay: "0.3s" }}><PodcastingCard /></div>
            <div className={`${enter("anim-pop")} absolute`} style={{ left: 1197, top: 417, width: 190, animationDelay: "0.36s" }}><CommunityCountCard /></div>
            <div className={`${enter("anim-pop")} absolute`} style={{ left: 533, top: 653, width: 310, animationDelay: "0.42s" }}><FeaturedCreatorCard /></div>
          </div>
        </div>

        {/* ── below lg: stacked ── */}
        <div className="px-6 py-12 sm:px-10 lg:hidden">
          <div className="flex flex-col items-center text-center">
            <div className={`${enter("anim-rise")} inline-flex h-[47px] items-center justify-center rounded-[45px] border border-black px-7 font-poppins text-[13px] uppercase text-black sm:text-[16px]`}>
              Community
            </div>

            <h2 className={`${enter("anim-rise")} mt-5 font-poppins text-[38px] font-medium leading-[1.22] text-black sm:text-[48px]`} style={{ animationDelay: "0.06s" }}>
              Where{" "}
              <span className="relative inline-block">
                <span className="absolute -inset-x-2 inset-y-1 rounded-[17.812px]" style={{ background: BLUE }} aria-hidden />
                <span className="relative">Creators</span>
              </span>
              <br />
              Grow Together.
            </h2>

            <p className={`${enter("anim-rise")} mt-5 max-w-[551px] font-poppins text-[16px] font-light leading-7 text-black`} style={{ animationDelay: "0.14s" }}>
              Join a thriving community of creators, mentors, and learners. Collaborate, connect, and grow through shared opportunities.
            </p>
          </div>

          {/* Full-bleed portrait carrying all five cards that used to stack below
              it — stacked they made this section roughly three phone screens tall.
              The cards use their `compact` variant so five fit around the subject.

              No background of its own: the panel's colour is meant to show through
              the cutout PNG's transparent area.

              Layering is DOM order, not z-index — the two cards declared before
              the portrait tuck behind the subject and read as partly hidden, the
              three after it stay fully visible. The head filling the space between
              the two rows is what closes the dead gap the earlier version had. */}
          <div
            className={`${enter("anim-pop")} relative -mx-6 mt-0 h-[460px] overflow-hidden min-[420px]:h-[700px] sm:-mx-10 sm:h-[780px]`}
            style={{ animationDelay: "0.2s" }}
          >
            {/* ── behind the subject ── */}
            <div className="absolute left-4 top-[20%] w-[150px] min-[420px]:w-[164px] sm:left-8 sm:w-[198px]">
              <LiveSessionCard compact />
            </div>
            <div className="absolute right-4 top-[10%] w-[150px] min-[420px]:w-[164px] sm:right-8 sm:w-[198px]">
              <PodcastingCard compact />
            </div>

            {/* object-cover instead of the stage's percentage crop: the source is
                950×633, so covering a tall frame shows its full height and trims
                the sides — no distortion, and it never needs the 937:788 host
                frame the desktop crop depends on. */}
            <img
              src="/images/community-hero.png"
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-top"
              aria-hidden
            />

            {/* ── in front of the subject ──
                Dropped below 420px: the stage is only 460px tall there, which is
                room for the top pair and the Featured card but not this middle
                row — it is what was landing under the Featured card on smaller
                phones. They come back at 420px, where the stage is 700px. */}
            <div className="absolute left-4 top-[52%] hidden w-[150px] min-[420px]:block min-[420px]:w-[164px] sm:left-8 sm:w-[198px]">
              <CommunityCountCard compact />
            </div>
            <div className="absolute right-4 top-[52%] hidden w-[150px] min-[420px]:block min-[420px]:w-[164px] sm:right-8 sm:w-[198px]">
              <CreatorCourseCard compact />
            </div>

            <div className="absolute inset-x-4 bottom-4 sm:inset-x-8 sm:bottom-6">
              <FeaturedCreatorCard compact />
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-3.5">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className={`${enter("anim-rise")} rounded-[16px] bg-white/70 px-4 py-3.5`}
                style={{ animationDelay: `${0.26 + i * 0.06}s` }}
              >
                <FeatureRow f={f} />
              </div>
            ))}
          </div>

          <div className={`${enter("anim-rise")} mt-9`} style={{ animationDelay: "0.44s" }}>
            <JoinCta className="w-full sm:w-[270px]" />
          </div>

        </div>
      </div>
    </section>
  );
}
